import {useFluxStore} from 'client/framework';
import React, {useRef, useState} from 'react';
import {Badge, Icon, WithTooltip} from '@albert-io/atomic';
import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';
import sessionStore from 'client/Session/SessionStore';

import {useAdminContentLocks} from './useAdminContentLocks';
import {isEffectivelyLocked} from './contentLockDerivation';
import type {GuideTree} from './contentLockDerivation';
import {ManageRestrictionsModal} from './ManageRestrictionsModal';
import {RestrictionMenu} from './RestrictionMenu';

/** Feature flag gating the entire admin-managing-restrictions experience (indicators, action, modal). */
export const ADMIN_MANAGING_RESTRICTIONS_FLAG = 'admin_managing_restrictions';

// Per design (#17841) the admin indicator is a single solid lock; full and partial restriction
// both show the same lock (the per-school breakdown popover conveys the difference).
export const getLockVariant = (
  restrictedCount: number
): {icon: 'lock'; iconStyle: 'solid'} | null => {
  if (restrictedCount === 0) {
    return null;
  }
  return {icon: 'lock', iconStyle: 'solid'};
};

interface RestrictionIndicatorProps {
  guideLevelName: string;
  /** Lock source id. Drives the breakdown + whether the lock shows; absent → renders nothing. */
  guideLevelId?: string;
  /**
   * Where the indicator lives. Every non-`'guide'` variant is a "question variant" — same styling +
   * guide-link menu, differing only in where the caller renders the lock. `'guide'` opens the modal.
   */
  variant?: 'guide' | 'question' | 'student-submission' | 'question-metadata';
  /** (question variants) URL for the "Edit restriction settings in the assessment guide" CTA. */
  assessmentGuideHref?: string;
  placement?:
    | 'bottom-start'
    | 'bottom'
    | 'bottom-end'
    | 'top-start'
    | 'top'
    | 'right-start'
    | 'right'
    | 'left-start'
    | 'left';
  /**
   * Portal the tooltip content. Defaults to `false` (content stays a descendant so the edit action
   * stays clickable); set `true` inside a virtualized/transformed list where ancestor transforms
   * break inline fixed-positioning.
   */
  usePortal?: boolean;
  /**
   * (question variants) Extra guide-level ids checked alongside `guideLevelId` — a question's aligned
   * levels, so a lock on the level OR any ancestor shows it restricted. (Guide variant uses `guideLevelTree`.)
   */
  ancestorGuideLevelIds?: string[];
  /**
   * (guide variant) The guide's level tree, enabling full effective-lock derivation: locked if the
   * level, an ancestor, or all of its children are locked (downward + upward cascade).
   */
  guideLevelTree?: GuideTree;
  /**
   * Render as a "Restricted at N schools" badge instead of the bare lock icon. Opt-in — only the
   * subject-assessments guide-level list uses the badge; every other surface keeps the compact icon.
   */
  showAsBadge?: boolean;
}

/**
 * Lock shown next to a restricted assessment guide level or question (admin-locked-assessments,
 * Part 2). Clicking it opens a menu with the per-school restricted/unrestricted breakdown (and, for
 * the guide variant, an "Edit Settings" action). Click-to-open (not hover) so the menu stays put
 * while the pointer moves into it; it closes on an outside click or a second click on the lock.
 */
export const RestrictionIndicator = ({
  guideLevelName,
  guideLevelId,
  ancestorGuideLevelIds,
  guideLevelTree,
  variant = 'guide',
  assessmentGuideHref,
  placement = 'bottom-start',
  usePortal = false,
  showAsBadge = false
}: RestrictionIndicatorProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const tooltipRef = useRef<React.ElementRef<typeof WithTooltip>>(null);
  const {isFeatureEnabled} = useIsFeatureFlagEnabled(ADMIN_MANAGING_RESTRICTIONS_FLAG);
  const isAdmin = useFluxStore(sessionStore, (s) => s.isSchoolAdministrator());
  const enabled = isAdmin && isFeatureEnabled;

  const {loading, getBreakdown, getBreakdownWhere} = useAdminContentLocks({enabled});
  // With the tree, derive full effective-lock state; otherwise a lock on the level or any aligned id counts.
  const {restricted, unrestricted} =
    guideLevelTree && guideLevelId
      ? getBreakdownWhere((lockedSourceIds) =>
          isEffectivelyLocked(guideLevelTree, guideLevelId, lockedSourceIds)
        )
      : getBreakdown(
          [guideLevelId, ...(ancestorGuideLevelIds ?? [])].filter((id): id is string => Boolean(id))
        );
  const lockVariant = getLockVariant(restricted.length);

  if (!enabled || loading || !lockVariant) {
    return null;
  }

  const isQuestionVariant = variant !== 'guide';
  const restrictedSchoolCount = restricted.length;
  const restrictionBadgeLabel = `Restricted at ${restrictedSchoolCount} ${
    restrictedSchoolCount === 1 ? 'school' : 'schools'
  }`;

  return (
    <>
      <WithTooltip
        ref={tooltipRef}
        behavior='click'
        theme='light'
        placement={placement}
        allowOnlyOneTooltip
        anchoredFixedPositionProps={{
          usePortal,
          // Anchor flip/overflow to the viewport so a side tooltip in a narrow list doesn't flip back over it.
          modifiers: {
            preventOverflow: {boundariesElement: 'viewport'},
            flip: {boundariesElement: 'viewport'}
          }
        }}
        content={
          <RestrictionMenu
            restricted={restricted}
            unrestricted={unrestricted}
            onClose={() => tooltipRef.current?.deactivate()}
            {...(isQuestionVariant ? {assessmentGuideHref} : {onEdit: () => setIsModalOpen(true)})}
          />
        }
      >
        {/* WithTooltip wraps this trigger in its own clickable wrapper (handles the open); the button
            here carries the a11y (name + aria-haspopup) and keyboard support. */}
        {showAsBadge ? (
          // DQA #17841: render the interactive/clickable neutral Badge, not a static bordered label.
          // Name comes from the badge text, so no aria-label (it would mismatch the visible text).
          <button
            type='button'
            aria-haspopup='menu'
            className='group mt-x1 p-x0 inline-flex cursor-pointer appearance-none border-0 bg-transparent'
          >
            <Badge
              icon={lockVariant.icon}
              iconStyle={lockVariant.iconStyle}
              className='!cursor-pointer transition-opacity group-hover:opacity-80!'
            >
              {restrictionBadgeLabel}
            </Badge>
          </button>
        ) : (
          // DQA #17841: bare lock icon in a faded chip so the clickable affordance reads.
          <button
            type='button'
            aria-label='Restriction details'
            aria-haspopup='menu'
            className='rounded-x0-half bg-background-subtle p-x0-half inline-flex cursor-pointer appearance-none items-center justify-center border-0 text-xs transition-opacity hover:opacity-80'
          >
            <Icon
              icon={lockVariant.icon}
              iconStyle={lockVariant.iconStyle}
              color='secondary'
              size='inherit'
            />
          </button>
        )}
      </WithTooltip>
      {!isQuestionVariant && isModalOpen && (
        <ManageRestrictionsModal
          guideLevelName={guideLevelName}
          guideLevelId={guideLevelId}
          guideLevelTree={guideLevelTree}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};

export default RestrictionIndicator;
